import React, { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./energy-requirements.module.scss";
import { Appliance } from "../../../../../model/appliance";
import { CombinedAppliance } from "../../../../../model/combined-appliance-category";
import { SelectedApplianceConfig } from "./energy-requirements.types";
import { SearchIcon } from "../../../../../assets/icons";
import { Button } from "../../../components";

interface SelectApplianceProps {
	appliances: CombinedAppliance[];
	selectedAppliances: SelectedApplianceConfig[];
	isLoading: boolean;
	onSelectAppliance: (appliance: Appliance) => void;
	onCancel: () => void;
	onContinue: () => void;
}

export const SelectAppliance: React.FC<SelectApplianceProps> = ({ appliances, selectedAppliances, isLoading, onSelectAppliance, onCancel, onContinue }) => {
	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search);
		}, 500);
		return () => clearTimeout(timer);
	}, [search]);

	const isApplianceSelected = useCallback((applianceId: number) => selectedAppliances.some(sa => sa.applianceId === applianceId), [selectedAppliances]);

	const getApplianceConfig = useCallback((applianceId: number) => selectedAppliances.find(sa => sa.applianceId === applianceId), [selectedAppliances]);

	const allAppliances = useMemo(() => {
		const flat: Appliance[] = [];
		for (const item of appliances) {
			if (item.isCategory()) {
				const category = item.toApplianceCategory();
				if (category.appliances) {
					flat.push(...category.appliances);
				}
			} else {
				flat.push(item.toAppliance());
			}
		}
		return flat;
	}, [appliances]);

	const filteredAppliances = useMemo(() => {
		if (!debouncedSearch) return allAppliances;
		const searchTerm = debouncedSearch.toLowerCase();
		return allAppliances.filter(a => a.name.toLowerCase().includes(searchTerm));
	}, [allAppliances, debouncedSearch]);

	return (
		<div className={styles.container}>
			<div className={styles.content}>
				<div className={styles.header}>
					<div className={styles.iconWrapper}>
						<span className={styles.questionMark}>&#10067;</span>
					</div>
					<div className={styles.titleSection}>
						<h2 className={styles.title}>Select Appliance</h2>
					</div>
				</div>

				<div className={styles.searchWrapper}>
					<div className={styles.searchIcon}>
						<SearchIcon />
					</div>
					<input type="text" className={styles.searchInput} placeholder="Search appliances" value={search} onChange={e => setSearch(e.target.value)} />
				</div>

				<div className={styles.applianceListSection}>
					<span className={styles.sectionLabel}>All Appliances</span>
					<div className={styles.applianceList}>
						{isLoading && <p className={styles.subtitle}>Loading appliances...</p>}
						{!isLoading &&
							filteredAppliances.map(appliance => (
								<div key={appliance.id} className={styles.applianceRow}>
									<div className={styles.applianceLeft}>
										{appliance.iconUrl && <img src={appliance.iconUrl} alt={appliance.name} className={styles.applianceIcon} />}
										<span className={styles.applianceName}>{appliance.name}</span>
									</div>
									{isApplianceSelected(appliance.id) ? (
										<div className={styles.categoryBadge}>
											<span className={styles.categoryBadgeCount}>
												{getApplianceConfig(appliance.id)?.quantity}/{getApplianceConfig(appliance.id)?.dailyUsage}
											</span>
										</div>
									) : (
										<button className={styles.addButton} onClick={() => onSelectAppliance(appliance)}>
											Add
										</button>
									)}
								</div>
							))}
						{!isLoading && filteredAppliances.length === 0 && <p className={styles.subtitle}>No appliances found</p>}
					</div>
				</div>

				<div className={styles.landingFooter}>
					<div className={styles.landingFooterInner}>
						<Button onClick={onCancel} variant="secondary">
							Cancel
						</Button>
						<Button onClick={onContinue} disabled={selectedAppliances.length === 0} variant="primary">
							Continue
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
